<template>
  <CustomTable ref="table" class="business-table" :elements=elements :data="data" :searchOptions="searchOptions" :loadData="loadData">
    <template v-slot:table>
      <thead>
        <tr>
          <custom-header field="creditNoteNumber" class="col-fit">Avoir</custom-header>
          <custom-header field="invoiceNumber" class="hide-for-mobile col-fit">Facture</custom-header>
          <custom-header field="number" class="hide-for-mobile col-fit">Devis</custom-header>
          <custom-header field="title" class="hide-for-mobile col-fill">Titre</custom-header>
          <custom-header field="creditNoteStatus" class="hide-for-mobile col-fit">Status</custom-header>
          <custom-header field="clientName" class="col-fit">Client</custom-header>
          <custom-header field="creditNoteAmount" class="hide-for-mobile col-fit">Montant</custom-header>
          <custom-header field="creditNoteToRefund" class="col-fit">Solde</custom-header>
        </tr>
      </thead>
      <tbody v-if="!isLoading && data && data.length > 0">
        <tr v-for="(business, index) in $refs.table.$refs.searchArray.simpleFilter(data)" :key="index" class="business-row" @click="openBusiness(business)">
          <custom-body field="creditNoteNumber" class="col-fit">
            <DocumentChips :credit-note-numbers="[business.creditNoteNumber]" />
          </custom-body>
          <custom-body field="invoiceNumber" class="hide-for-mobile col-fit">
            <DocumentChips :invoice-number="business.invoiceNumber" />
          </custom-body>
          <custom-body field="number" class="hide-for-mobile col-fit">
            <QuoteNumber :number="business.number" :order-number="haveProductionOption ? business.orderNumber : undefined" />
          </custom-body>
          <custom-body field="title" class="hide-for-mobile col-fill" :title="business.title">{{ business.title ? business.title : '' }}</custom-body>
          <custom-body field="creditNoteStatus" class="hide-for-mobile col-fit"><StatusBadge :status="business.creditNoteStatus" :status-enum="CREDIT_NOTE_STATUS_ENUM" /></custom-body>
          <custom-body field="clientName" class="col-fit cell-strong col-truncate" :title="clientNameOf(business)">{{ clientNameOf(business) }}</custom-body>
          <custom-body field="creditNoteAmount" class="hide-for-mobile col-fit cell-strong">{{ business.creditNoteAmount ? formatAmount(-business.creditNoteAmount) : '-' }}</custom-body>
          <custom-body field="creditNoteToRefund" class="col-fit">{{ business.creditNoteToRefund ? formatAmount(business.creditNoteToRefund) : '-' }}</custom-body>
        </tr>
      </tbody>
      <tbody v-else><tr class="empty-row">
        <td colspan="8" class="hide-for-mobile">Aucun avoir</td>
      </tr></tbody>
    </template>
  </CustomTable>
  <BusinessManagementModal ref="businessManagementModal" :callback=loadData />
</template>

<script>
import {BusinessController} from "@/controllers/business/BusinessController";
import BusinessManagementModal from "@/components/modals/BusinessManagementModal.vue";
import CustomTable from "@/components/CustomTable.vue";
import CustomHeader from "@/components/tables/CustomHeader.vue";
import CustomBody from "@/components/tables/CustomBody.vue";
import StatusBadge from "@/components/tables/commons/StatusBadge.vue";
import DocumentChips from "@/components/tables/commons/DocumentChips.vue";
import QuoteNumber from "@/components/tables/commons/QuoteNumber.vue";
import {CREDIT_NOTE_STATUS_ENUM} from "@/models/creditNoteStatusEnum";
import {BUSINESS_DOCUMENT_TYPE_ENUM} from "@/models/businessDocumentTypeEnum";
import {SORT_OPERATOR_ENUM} from "@/models/sortOperatorEnum";
import {COUNTS_REFRESH_EVENT} from "@/components/businessTabs";

/**
 * Tableau des avoirs : une ligne par avoir (un contrat peut en avoir plusieurs, chacun a sa ligne et son statut).
 * Un clic sur la ligne ouvre la modale du contrat directement sur cet avoir. Le back renvoie toutes les lignes.
 */
export default {
  name: 'CreditNoteTable',
  components: {CustomBody, CustomHeader, CustomTable, StatusBadge, DocumentChips, QuoteNumber, BusinessManagementModal},
  computed: {
    CREDIT_NOTE_STATUS_ENUM() {
      return CREDIT_NOTE_STATUS_ENUM;
    },
    haveProductionOption() {
      return localStorage && localStorage.options && localStorage.options.includes('production');
    }
  },
  data() {
    return {
      data: [],
      elements: [
        {label: "N°Facture", value: "invoiceNumber", type: "number"},
        {label: "N°Devis", value: "number", type: "number"},
        {label: "N°Commande", value: "orderNumber", type: "number"},
        {label: "Titre", value: "title", type: "text"},
        {label: "Client", value: "clientName", type: "text"},
        {label: "N°Avoir", value: "creditNoteNumber", type: "number", sortOnly: true},
        {label: "Status", value: "creditNoteStatus", type: "text", sortOnly: true},
        {label: "Montant", value: "creditNoteAmount", type: "number", sortOnly: true},
        {label: "Solde", value: "creditNoteToRefund", type: "number", sortOnly: true},
      ],
      searchOptions: {
        filters: [],
        sorts: [{field: "creditNoteNumber", order: SORT_OPERATOR_ENUM.DESCENDING.value}]
      },
      isLoading: true,
      hasLoadedOnce: false
    };
  },
  async mounted() {
    this.$refs.table.$refs.searchArray.initWithURLParams();

    this.$refs.table.$refs.paginationArray.paginationInputs.pages.readOnly = true;
    this.$refs.table.$refs.paginationArray.isNextReloadCancel = true;
    this.$refs.table.$refs.paginationArray.paginationInputs.pages.value = undefined;
    this.$refs.table.$refs.paginationArray.index = undefined;

    await this.loadData();

    // Clean URL without reload
    const url = new URL(window.location.href);
    window.history.replaceState({}, '', url.origin + url.pathname);
  },
  methods: {
    clientNameOf(business) {
      return business.currentClientName ? business.currentClientName : (business.clientName ? business.clientName : '');
    },
    formatAmount(value) {
      return value.toLocaleString('fr-FR', {minimumFractionDigits: 2, maximumFractionDigits: 2}) + ' €';
    },
    async loadData() {
      this.$refs.table?.startLoading();
      this.isLoading = true;
      this.data = [];

      const resp = await BusinessController.getCreditNoteTable(this.$refs.table.buildURLParams());
      if (resp && !resp.errors) {
        this.data.push(...resp);
      }

      this.isLoading = false;
      this.$refs.table?.stopLoading();
      // Les compteurs du menu ne sont recalculés que si les données ont changé (pas à l'ouverture de la rubrique)
      if (this.hasLoadedOnce) window.dispatchEvent(new Event(COUNTS_REFRESH_EVENT));
      this.hasLoadedOnce = true;
    },
    // Le tableau ne reçoit que les champs affichés : la modale a besoin du contrat complet.
    async openBusiness(business) {
      if (!business.id) return;
      const fullBusiness = await BusinessController.get(business.id);
      if (fullBusiness && !fullBusiness.errors) {
        this.$refs.businessManagementModal.openModal(fullBusiness, {type: BUSINESS_DOCUMENT_TYPE_ENUM.CREDIT_NOTE.value, creditNoteNumber: business.creditNoteNumber});
      }
    }
  }
}
</script>

<style lang="stylus" scoped>
@require '~/src/common/style/common.styl';
@require '~/src/common/style/businessTable.styl';
</style>
